import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import './ParticleField.css';

const ParticleField = ({ particleCount = 100, color = '#ff6b35' }) => {
  const mountRef = useRef(null);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const [, setMousePosition] = useState({ x: 0, y: 0 }); // Keep state for re-renders if needed

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true, 
      antialias: true 
    });

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);
    const mountElement = mountRef.current; // Capture the mount element
    mountElement.appendChild(renderer.domElement);

    // Create particles
    const particles = new THREE.BufferGeometry();
    const positions = [];
    const velocities = [];

    for (let i = 0; i < particleCount; i++) {
      positions.push(
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10
      );
      velocities.push(
        (Math.random() - 0.5) * 0.02,
        (Math.random() - 0.5) * 0.02,
        (Math.random() - 0.5) * 0.02
      );
    }

    particles.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

    const particleMaterial = new THREE.PointsMaterial({
      color: color,
      size: 0.05,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending
    });

    const particleSystem = new THREE.Points(particles, particleMaterial);
    scene.add(particleSystem);

    camera.position.z = 5;

    // Mouse interaction
    const handleMouseMove = (event) => {
      const newPosition = {
        x: (event.clientX / window.innerWidth) * 2 - 1,
        y: -(event.clientY / window.innerHeight) * 2 + 1
      };
      mousePositionRef.current = newPosition;
      setMousePosition(newPosition); // Update state if components depend on it
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Animation
    let animationId;
    const animate = () => {
      animationId = requestAnimationFrame(animate);

      const positions = particleSystem.geometry.attributes.position.array;

      for (let i = 0; i < positions.length; i += 3) {
        // Update positions
        positions[i] += velocities[i];
        positions[i + 1] += velocities[i + 1];
        positions[i + 2] += velocities[i + 2];

        // Bounce off boundaries
        if (Math.abs(positions[i]) > 5) velocities[i] *= -1;
        if (Math.abs(positions[i + 1]) > 5) velocities[i + 1] *= -1;
        if (Math.abs(positions[i + 2]) > 5) velocities[i + 2] *= -1;

        // Mouse interaction
        const dx = positions[i] - mousePositionRef.current.x * 5;
        const dy = positions[i + 1] - mousePositionRef.current.y * 5;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 2) {
          positions[i] += dx * 0.01;
          positions[i + 1] += dy * 0.01;
        }
      }

      particleSystem.geometry.attributes.position.needsUpdate = true;
      particleSystem.rotation.y += 0.001;

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationId);
      if (mountElement && renderer.domElement) {
        mountElement.removeChild(renderer.domElement);
      }
      particles.dispose();
      particleMaterial.dispose();
      renderer.dispose();
    };
  }, [particleCount, color]); // Dependencies for the effect - mousePositionRef is stable and doesn't need to be in the dependency array

  return <div ref={mountRef} className="particle-field" />;
};

export default ParticleField;
